// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { xIsAuthenticated } from "@tps/App/Store/Selectors/GeneralSelectors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    companyName: state.components.contactUsForm.fieldValues["companyName"],
    // xng-change
    billTo: getCurrentBillToState(state).value,
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CompanyNameStyles {
    companyNameTextField?: TextFieldPresentationProps;
}

export const companyNameStyles: CompanyNameStyles = {};

const styles = companyNameStyles;

const CompanyName: React.FC<Props> = ({ fields, companyName, setFieldValue, isAuthenticated, billTo }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [companyNameErrorMessage, setCompanyNameErrorMessage] = React.useState("");

    React.useEffect(() => {
        if (isAuthenticated && billTo && billTo.companyName) {
            setFieldValue({ key: "companyName", value: billTo.companyName });
        }
    }, [isAuthenticated, billTo?.companyName, setFieldValue]);

    React.useEffect(() => {
        contactFormContext.validators.companyName = validateCompanyName;
        if (typeof companyName !== "undefined") {
            validateCompanyName();
        }
    }, [companyName]);

    React.useEffect(() => {
        contactFormContext.validators.companyName = validateCompanyName;
    }, [isRequired]);

    const validateCompanyName = () => {
        const errorMessage = isRequired && !companyName ? (siteMessage("Field_Required", label) as string) : "";
        setCompanyNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const companyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "companyName", value: event.target.value });
    };

    return (
        <TextField
            {...styles.companyNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={companyName || ""}
            error={companyNameErrorMessage}
            onChange={companyNameChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompanyName),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Company Name",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Company Name",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
